import React, { useCallback } from 'react';

import { AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

import { Modal } from '@pkg/components';
import { Button } from '@pkg/components/Sections/Profile/Buttons';
import { useAppDispatch, useAppSelector, useDeleteWishlist } from '@pkg/hooks';

import { type WishlistMetadataResponse } from '@app/client/resources/wishlists/types';
import { wishlistActions } from '@app/store/slices';

import { ActionsWrapper, Content, FaceScreamingIcon, Heading, SubHeading } from './styles';

interface Props {
    wishlistMetadata: WishlistMetadataResponse;
}

export const DeleteWishlistModal = ({ wishlistMetadata }: Props) => {
    const dispatch = useAppDispatch();
    const showDeleteModal = useAppSelector((state) => state.wishlist.showDeleteModal);
    const { mutateAsync: deleteWishlist, isPending, isSuccess } = useDeleteWishlist();
    const navigate = useNavigate();
    const wishlistName = wishlistMetadata?.name ?? null;

    const handleCloseDelete = useCallback(() => {
        dispatch(wishlistActions.setShowDeleteModal(false));
    }, [dispatch]);

    const handleDelete = () => {
        deleteWishlist({ wishlist_id: wishlistMetadata.id }).then(() => {
            navigate('/wishlists/my-wishlists');
            dispatch(wishlistActions.setDeleteWishlistName(wishlistName));
            dispatch(wishlistActions.setShowDeleteWishListToast(true));
            handleCloseDelete();
        });
    };

    return (
        <AnimatePresence mode="wait">
            {showDeleteModal && (
                <Modal onClose={handleCloseDelete} mobileDarkBackground>
                    <Content>
                        <FaceScreamingIcon emojiID="😱" />
                        <Heading>Are you sure?</Heading>
                        <SubHeading>Once you delete a Wishlist and all its items, you can’t undo it!</SubHeading>
                        <ActionsWrapper>
                            <Button onClick={handleCloseDelete} color="secondary">
                                Cancel
                            </Button>
                            <Button onClick={handleDelete} loading={isPending || isSuccess}>
                                Delete forever
                            </Button>
                        </ActionsWrapper>
                    </Content>
                </Modal>
            )}
        </AnimatePresence>
    );
};
